import { add, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import type { Duration } from 'date-fns';
import type { VoidFunctionComponent } from 'react';

import { verify_subscription } from '../../../../utils';
import { icons, Select } from '../../../generic';
import FormGroup from '../../../generic/Forms/FormGroup';
import Tooltip from '../../../generic/Tooltip';
import TooltipIcon from '../../../generic/TooltipIcon';
import type { SelectOption, SetState } from '../../../../types';

import styles from './IgnoreOptions.module.less';

type ExpirationOption = SelectOption<Duration | null>;

const expirationOptions: Array<{ label: string; value: Duration | null }> = [
  {
    label: 'Never',
    value: null,
  },
  {
    label: '1 day',
    value: { days: 1 },
  },
  {
    label: '1 week',
    value: { days: 7 },
  },
  {
    label: '1 month',
    value: { months: 1 },
  },
  {
    label: '1 quarter (90 days)',
    value: { days: 90 },
  },
  {
    label: '1 year',
    value: { years: 1 },
  },
];

type Props = {
  setExpirationDate: SetState<string | null>;
  startDate?: string;
  shouldDisableNeverOption?: boolean;
};

const ExpirationOptions: VoidFunctionComponent<Props> = ({
  setExpirationDate,
  startDate,
  shouldDisableNeverOption,
}) => {
  const expirationOptionsToUse = shouldDisableNeverOption ? expirationOptions.slice(1) : expirationOptions;

  const [expiration, setExpiration] = useState<ExpirationOption>(expirationOptionsToUse[0] as ExpirationOption);
  const isPremium = verify_subscription('premium');

  useEffect(() => {
    if (expiration.value) {
      setExpirationDate(
        formatInTimeZone(
          add(startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : new Date(), expiration.value),
          'Etc/Greenwich',
          'yyyy-MM-dd',
        ),
      );
    } else {
      setExpirationDate(null);
    }
  }, [expiration, setExpirationDate, startDate]);

  return (
    <div className={styles.expirationOptions}>
      <FormGroup
        legend={
          <div>
            When does it expire?{' '}
            <TooltipIcon
              id="expiration-tooltip"
              icon={icons.circleQuestion}
              tooltip="The Ignore Rule will expire at midnight UTC on the specified day and issues will be unignored."
            />
          </div>
        }
        spacing="sm"
      >
        <Tooltip
          tooltip="Temporary Ignore Rules require a premium subscription."
          showTooltip={!isPremium}
          id="ignore-rules-expiration-tooltip"
        >
          <span>
            <Select
              id="ignore-rule-expiration-select"
              options={expirationOptionsToUse}
              defaultValue={expirationOptionsToUse[0]}
              value={expiration}
              onChange={(option) => {
                if (option) {
                  setExpiration(option);
                }
              }}
              isDisabled={!isPremium}
            />
          </span>
        </Tooltip>
      </FormGroup>
    </div>
  );
};

export default ExpirationOptions;
